// src/components/ProtectedRoute.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { createSelector } from 'reselect';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { validateStoredToken } from '../store/auth/thunks';
import { controller } from '../api/controller';
import { usePublicUiLocale } from '../contexts/PublicUiLocaleContext';
import {
  ENTERPRISE_MODE,
  isEnterpriseModeEnabledFromConfig,
} from '../constants/featureFlags';

// Селектор с правильной мемоизацией
const selectAuthState = createSelector(
  [state => state.authSlice],
  (authSlice) => ({
    result: authSlice?.result ?? false,
    role: authSlice?.role ?? null,
    isLoading: authSlice?.isLoading ?? false,
    userName: authSlice?.userName ?? '',
    email: authSlice?.email ?? null,
    mustVerifyEmail: !!authSlice?.mustVerifyEmail,
    emailVerified: !!authSlice?.emailVerified,
  })
);

function ProtectedRoute({
  children,
  adminOnly,
  superAdminOnly,
  supportOnly,
  projectAdminPanel,
  projectStoreAccess,
}) {
  const { result, role, isLoading, userName, email, mustVerifyEmail, emailVerified } =
    useSelector(selectAuthState);
  const { lang, login: t } = usePublicUiLocale();
  const location = useLocation();
  const dispatch = useDispatch();
  const [isValidating, setIsValidating] = useState(false);
  const validationAttemptedRef = useRef(false);
  const [notice, setNotice] = useState('');
  const [bindEmailModalOpen, setBindEmailModalOpen] = useState(false);
  const [bindEmailValue, setBindEmailValue] = useState('');
  const [bindBusy, setBindBusy] = useState(false);
  const [publicRegistrationEnabled, setPublicRegistrationEnabled] = useState(
    !ENTERPRISE_MODE,
  );

  useEffect(() => {
    if (result || isLoading || isValidating || validationAttemptedRef.current) return;
    const storedToken = localStorage.getItem('token') || sessionStorage.getItem('token');
    if (!storedToken) return;
    validationAttemptedRef.current = true;
    setIsValidating(true);
    dispatch(validateStoredToken())
      .finally(() => setIsValidating(false));
  }, [result, isLoading, isValidating, dispatch]);

  useEffect(() => {
    let mounted = true;
    controller
      .getPublicAuthConfig()
      .then((cfg) => {
        if (!mounted) return;
        setPublicRegistrationEnabled(!isEnterpriseModeEnabledFromConfig(cfg));
      })
      .catch(() => {
        if (!mounted) return;
        setPublicRegistrationEnabled(!ENTERPRISE_MODE);
      });
    return () => {
      mounted = false;
    };
  }, []);

  if (isLoading || isValidating) {
    return <div>Загрузка...</div>;
  }

  if (!result) {
    const match = location.pathname.match(/^\/p\/([^/]+)/);
    const projectSlug = match ? match[1] : null;
    const target = projectSlug ? `/p/${projectSlug}/login` : '/';
    return <Navigate to={target} state={{ from: location }} replace />;
  }

  if (superAdminOnly && role !== 'superadmin') {
    const match = location.pathname.match(/^\/p\/([^/]+)/);
    const slug = match ? match[1] : null;
    if (role === 'admin') return <Navigate to={slug ? `/p/${slug}/admin` : '/'} replace />;
    return <Navigate to={slug ? `/p/${slug}/groups` : '/'} replace />;
  }

  if (adminOnly && role !== 'admin' && role !== 'superadmin') {
    const match = location.pathname.match(/^\/p\/([^/]+)/);
    const slug = match ? match[1] : null;
    return <Navigate to={slug ? `/p/${slug}/groups` : '/'} replace />;
  }

  // projectAdminPanel: единая project-scoped панель для admin + superadmin.
  if (projectAdminPanel && role !== 'admin' && role !== 'superadmin') {
    const match = location.pathname.match(/^\/p\/([^/]+)/);
    const slug = match ? match[1] : null;
    return <Navigate to={slug ? `/p/${slug}/groups` : '/'} replace />;
  }

  // Module store: project admin, superadmin, support.
  if (projectStoreAccess && role !== 'admin' && role !== 'superadmin' && role !== 'support') {
    const match = location.pathname.match(/^\/p\/([^/]+)/);
    const slug = match ? match[1] : null;
    return <Navigate to={slug ? `/p/${slug}/groups` : '/'} replace />;
  }

  if (supportOnly && role !== 'support') {
    const match = location.pathname.match(/^\/p\/([^/]+)/);
    const slug = match ? match[1] : null;
    if (role === 'superadmin') return <Navigate to="/superadmin" replace />;
    if (role === 'admin') return <Navigate to={slug ? `/p/${slug}/admin` : '/'} replace />;
    return <Navigate to={slug ? `/p/${slug}/groups` : '/'} replace />;
  }

  const showVerifyNotice =
    publicRegistrationEnabled && mustVerifyEmail && !emailVerified;
  const isSuperadminMvpPage = location.pathname.startsWith('/v2/superadmin');
  const bindAlertTop = isSuperadminMvpPage ? 68 : 112;

  return (
    <>
      {showVerifyNotice && (
        <Box sx={{ position: 'fixed', top: 72, right: 16, zIndex: 1500, maxWidth: 420 }}>
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={async () => {
                  try {
                    await controller.resendVerificationEmail({
                      loginOrEmail: userName,
                      lang,
                    });
                    setNotice(t.resendSent);
                  } catch (e) {
                    setNotice(
                      e?.response?.data?.message ||
                        e?.message ||
                        t.resendFailed,
                    );
                  }
                }}
              >
                {t.resendVerification}
              </Button>
            }
          >
            {t.verifyRequiredSoft}
          </Alert>
          {notice ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              {notice}
            </Alert>
          ) : null}
        </Box>
      )}
      {publicRegistrationEnabled && role === 'superadmin' && !email ? (
        <Box sx={{ position: 'fixed', top: bindAlertTop, right: 16, zIndex: 1500, maxWidth: 420 }}>
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => setBindEmailModalOpen(true)}
              >
                {t.bindEmailAction}
              </Button>
            }
          >
            {t.bindEmailRequired}
          </Alert>
        </Box>
      ) : null}
      <Dialog
        open={bindEmailModalOpen}
        onClose={() => {
          if (!bindBusy) setBindEmailModalOpen(false);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{t.bindEmailModalTitle}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            fullWidth
            label={t.bindEmailPlaceholder}
            value={bindEmailValue}
            onChange={(e) => setBindEmailValue(e.target.value)}
          />
          {notice ? (
            <Alert severity="info" sx={{ mt: 1 }}>
              {notice}
            </Alert>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBindEmailModalOpen(false)}
            disabled={bindBusy}
          >
            {t.commonCancel}
          </Button>
          <Button
            variant="contained"
            disabled={bindBusy || !bindEmailValue.trim()}
            onClick={async () => {
              setBindBusy(true);
              try {
                await controller.bindEmail({
                  email: bindEmailValue.trim(),
                  lang,
                });
                setNotice(t.bindEmailSent);
                setBindEmailModalOpen(false);
              } catch (e) {
                setNotice(
                  e?.response?.data?.message ||
                    e?.message ||
                    t.bindEmailFailed,
                );
              } finally {
                setBindBusy(false);
              }
            }}
          >
            {t.bindEmailSend}
          </Button>
        </DialogActions>
      </Dialog>
      {children}
    </>
  );
}

ProtectedRoute.propTypes = {
  children: PropTypes.node,
  adminOnly: PropTypes.bool,
  superAdminOnly: PropTypes.bool,
  supportOnly: PropTypes.bool,
  projectAdminPanel: PropTypes.bool,
  projectStoreAccess: PropTypes.bool,
};

export default ProtectedRoute;
